import { ChangeEvent } from "react";

import { Checkbox } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { capitalize } from "@s-libs/micro-dash";

import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { FilterSection } from "@/search/Langston/FilterPanel/FilterSection";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { IReadingSkill } from "@/search/Langston/types/IReadingSkill";
import { readingSkillCheckboxCss } from "./ReadingSkills.styles";

const CheckboxWithTrackEvent = withTrackEvent(
  Checkbox,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Checkbox> & React.RefAttributes<HTMLInputElement>
>;

const getEventProperties = (skill: string) => ({
  objectType: objectTypes.content,
  actionPrefix: actionPrefixes.initiate,
  actionName: actionNames.filter,
  streamProperties: {
    description: `Filter search by the ${skill} reading skill`,
  },
});

export const ReadingSkills = () => {
  const {
    getters: { isDisabledFilters, readingSkills = [] },
    handlers: {
      hasSearchParamValue,
      toggleSearchParamValue,
      getSearchParamValues,
    },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  if (!readingSkills.length) return null;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    toggleSearchParamValue(
      LangstonParameterSchema.reading_skills.key,
      e.target.value,
    );
  };

  const hasNoReadingSkillsValues =
    (getSearchParamValues(LangstonParameterSchema.reading_skills.key) || [])
      .length === 0;

  return (
    <>
      {readingSkills && (
        <FilterSection
          title="Reading skills"
          defaultCollapsed={hasNoReadingSkillsValues}
        >
          {readingSkills.map((readingSkill: IReadingSkill) => (
            <CheckboxWithTrackEvent
              onChange={handleChange}
              key={readingSkill.id}
              label={capitalize(readingSkill.text)}
              value={readingSkill.id}
              disabled={isDisabledFilters}
              checked={hasSearchParamValue(
                LangstonParameterSchema.reading_skills.key,
                readingSkill.id,
              )}
              __cssFor={readingSkillCheckboxCss}
              dataProps={{
                "data-qa-selector": `select-${readingSkill.text.replaceAll(" ", "-")}-search-filter`,
              }}
              {...getEventProperties(readingSkill.text)}
            />
          ))}
        </FilterSection>
      )}
    </>
  );
};
